import React from 'react';
import styled from 'styled-components';
import type { ServerMetasType } from '../molecules/Article';

type Props = {
  serverMetas: ServerMetasType;
};

const SnsLinks: React.FC<Props> = ({ serverMetas }) => {
  const twitterUrl = buildTwitterUrl(serverMetas['twitter:site']);
  const facebookUrl = buildFacebookUrl(serverMetas['fb:page_id']);
  const appStoreUrl = buildAppStoreUrl(serverMetas['al:ios:app_store_id']);
  const googlePlayUrl = buildGooglePlayUrl(serverMetas['al:android:package']);

  const items: SnsItem[] = [
    { key: 'twitter', label: 'X', bg: '#111111', href: twitterUrl, icon: XIcon },
    { key: 'facebook', label: 'Facebook', bg: '#1877F2', href: facebookUrl, icon: FacebookIcon },
    { key: 'apple', label: 'App Store', bg: '#000000', href: appStoreUrl, icon: AppleIcon },
    { key: 'android', label: 'Google Play', bg: '#34A853', href: googlePlayUrl, icon: AndroidIcon },
  ];

  return (
    <Container>
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = Boolean(item.href);
        return (
          <SnsIcon
            key={item.key}
            as={isActive ? 'a' : 'div'}
            href={item.href}
            target={isActive ? '_blank' : undefined}
            rel={isActive ? 'noopener noreferrer' : undefined}
            aria-disabled={!isActive}
            $active={isActive}
            $bg={item.bg}
            title={item.label}
          >
            <Icon />
          </SnsIcon>
        );
      })}
    </Container>
  );
};

export default SnsLinks;

const Container = styled.section`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  margin: 16px 0 0;
`;

type SnsIconPropsType = {
  $active: boolean;
  $bg: string;
};

const SnsIcon = styled.a<SnsIconPropsType>`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: center;
  width: 46px;
  min-width: 46px;
  max-width: 46px;
  height: 46px;
  min-height: 46px;
  max-height: 46px;
  user-select: none;
  background: ${(props) => (props.$active ? props.$bg : '#e2e2e2')};
  color: ${(props) => (props.$active ? '#fff' : '#a0a0a0')};
  border-radius: 12px;
  text-decoration: none;
  transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;
  ${(props) => (!props.$active ? 'filter: grayscale(100%);' : '')}
  ${(props) => (props.$active ? 'cursor: pointer;' : 'cursor: default;')}
  &:hover {
    transform: ${(props) => (props.$active ? 'translateY(-1px)' : 'none')};
    box-shadow: ${(props) => (props.$active ? '0 4px 10px rgba(0,0,0,0.18)' : 'none')};
  }
  svg {
    width: 26px;
    height: 26px;
    fill: currentColor;
  }
  .icon-x {
    width: 22px;
    height: 22px;
  }
`;

type SnsItem = {
  key: string;
  label: string;
  bg: string;
  href?: string;
  icon: React.FC;
};

const buildTwitterUrl = (value: string): string | undefined => {
  const handle = value?.trim().replace(/^@/, '');
  return handle ? `https://twitter.com/${encodeURIComponent(handle)}` : undefined;
};

const buildFacebookUrl = (value: string): string | undefined => {
  const id = value?.trim();
  return id ? `https://www.facebook.com/${encodeURIComponent(id)}` : undefined;
};

const buildAppStoreUrl = (value: string): string | undefined => {
  const id = value?.trim();
  return id ? `https://apps.apple.com/app/id${encodeURIComponent(id)}` : undefined;
};

const buildGooglePlayUrl = (value: string): string | undefined => {
  const pkg = value?.trim();
  return pkg ? `https://play.google.com/store/apps/details?id=${encodeURIComponent(pkg)}` : undefined;
};

const XIcon: React.FC = () => (
  <svg className="icon-x" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26L22 21.75h-6.172l-4.833-6.32-5.53 6.32H2.155l7.73-8.84L2 2.25h6.33l4.37 5.78 5.544-5.78Zm-1.16 17.52h1.833L7.25 4.14H5.29l11.794 15.63Z" />
  </svg>
);

const FacebookIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M13.5 9.5V7.5c0-.6.4-1 1-1h1.5V3.5h-2.3c-2.3 0-3.7 1.5-3.7 3.8v2.2H8v3h2v8h3v-8h2.5l.5-3H13.5Z" />
  </svg>
);

const AppleIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M16.68 13.12c.02 2.2 1.93 2.93 1.95 2.94-.01.05-.3 1.05-1 2.08-.6.9-1.22 1.8-2.2 1.82-.95.02-1.25-.56-2.33-.56-1.08 0-1.42.54-2.31.58-.94.04-1.66-.98-2.27-1.88-1.24-1.8-2.2-5.08-.92-7.3.64-1.1 1.79-1.8 3.03-1.82.95-.02 1.85.62 2.33.62.47 0 1.37-.77 2.31-.66.4.02 1.51.16 2.23 1.2-.06.04-1.33.78-1.32 2.32ZM14.73 4.9c.52-.63.87-1.5.77-2.37-.75.03-1.65.5-2.2 1.13-.48.56-.9 1.45-.79 2.3.84.06 1.69-.43 2.22-1.06Z" />
  </svg>
);

const AndroidIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7.8 8.2 5.9 6.3a.6.6 0 1 1 .85-.85L8.7 7.3a6.7 6.7 0 0 1 6.6 0l1.95-1.85a.6.6 0 1 1 .85.85L16.2 8.2A6.6 6.6 0 0 1 18 12.5V16a2 2 0 0 1-2 2h-1v3a1 1 0 0 1-2 0v-3H11v3a1 1 0 0 1-2 0v-3H8a2 2 0 0 1-2-2v-3.5a6.6 6.6 0 0 1 1.8-4.3ZM9 11.5a.8.8 0 1 0 0-1.6.8.8 0 0 0 0 1.6Zm6 0a.8.8 0 1 0 0-1.6.8.8 0 0 0 0 1.6Z" />
  </svg>
);
